// Typed event endpoint functions. Backed by the canonical /events/*
// endpoints in specs/tier-04-interface/campaign-management.api.yaml
// (event-management lives under campaign-management per CHG-0729 module map).
//
// Consumed by @generated/screens/event-list.ts + event-detail.ts via
// the eventsApi namespace (tests use vi.spyOn(eventsApi, "listEvents")).
//
// /events/{id}/attendees + /events/{id}/delivery-logs are not yet
// canonical sub-resources — the v2 client bridges them as forward-
// promised stubs that return [] today. When canonical endpoints ship,
// swap each helper's body without touching page or test code.

import { apiClient } from "./client";
import type { PagedEnvelope } from "./types";

export type EventStatus =
  | "draft"
  | "scheduled"
  | "live"
  | "completed"
  | "canceled"
  | "archived";

export type EventType = "service" | "session" | "campaign" | "community" | "other";

export interface Event {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  event_type: EventType;
  status: EventStatus;
  starts_at: string;
  ends_at: string | null;
  timezone: string | null;
  location: string | null;
  // CHG-2450 (GAP-3b-ii-b) — EventRead returns these schedule/location
  // fields (src/api/app/schemas/event.py EventRead); modelled here so the
  // v2 EventEdit form can prefill them. Optional because the legacy v2
  // `location` field above is a stale projection the detail Overview tab
  // still reads — kept for back-compat, not written by the edit form.
  all_day?: boolean;
  is_virtual?: boolean;
  virtual_url?: string | null;
  location_name?: string | null;
  location_address?: string | null;
  registration_enabled: boolean;
  registration_link: string | null;
  max_attendees: number | null;
  registered_count: number;
  is_public: boolean;
  campaign_id: string | null;
  designation_id: string | null;
  // DRIFT-910 slice 1 (CHG-2457) — public-page content fields. EventRead returns
  // both (src/api/app/schemas/event.py); modelled here so the v2 EventEdit form
  // can prefill them. Optional for back-compat with the stale read projections.
  slug?: string | null;
  body?: string | null;
  // DRIFT-910 slice 3 (CHG-2478) — recurrence. EventRead returns recurrence
  // (enum) + cadence_rrule (RFC-5545) + recurrence_end_date; modelled here so
  // the v2 edit form can derive is_recurring + prefill the cadence picker.
  recurrence?: string | null;
  cadence_rrule?: string | null;
  recurrence_end_date?: string | null;
  // DRIFT-910 slice 4 (CHG-2483) — hero image FK (EventRead.hero_image_asset_id);
  // modelled here so the v2 edit form can prefill the asset-picker.
  hero_image_asset_id?: string | null;
  created_at: string;
  updated_at: string;
}

// CHG-2450 (GAP-3b-ii-b) — write payload for create (POST /events) + edit
// (PATCH /events/{id}). A v2-buildable SUBSET of the EventCreate/EventUpdate
// Pydantic schema (src/api/app/schemas/event.py): the fields whose canonical
// field-primitive exists (or is functional) in the v2 registry today.
// event_type / status are sent as the raw backend enum string values (the v2
// `EventType`/`EventStatus` unions above are a stale narrower projection used
// only by the read surfaces, so this payload intentionally types them as
// string). DRIFT-910 is being closed incrementally: slice 1 (CHG-2457) added
// slug + body; slice 2 (CHG-2462) added campaign_id + designation_id; slice 3
// (CHG-2478) adds recurrence (is_recurring toggle + cadence_rrule rrule-picker +
// recurrence_end_date); slice 4 (CHG-2483) adds hero_image_asset_id (asset-picker)
// — completing the buildable field parity (only the wizard-manifest codegen
// sub-gap of DRIFT-910 stays deferred).
export interface EventWritePayload {
  name?: string;
  description?: string | null;
  event_type?: string;
  status?: string;
  starts_at?: string | null;
  ends_at?: string | null;
  all_day?: boolean;
  timezone?: string | null;
  location_name?: string | null;
  location_address?: string | null;
  is_virtual?: boolean;
  virtual_url?: string | null;
  registration_enabled?: boolean;
  max_attendees?: number | null;
  registration_link?: string | null;
  is_public?: boolean;
  // DRIFT-910 slice 1 (CHG-2457) — public-page content.
  slug?: string | null;
  body?: string | null;
  // DRIFT-910 slice 2 (CHG-2462) — campaign / designation linkage.
  campaign_id?: string | null;
  designation_id?: string | null;
  // DRIFT-910 slice 3 (CHG-2478) — recurrence. is_recurring is a UI-only toggle
  // (no backend field); when off the form sends recurrence='none' and nulls
  // cadence_rrule/recurrence_end_date (mirrors v1 EventDetail.tsx
  // buildEventEditPayload, CHG-1505). cadence_rrule (the rrule-picker RRULE
  // string) is what the donor-facing iCal feed (RRULE:{cadence_rrule}) +
  // calendar list-query expand (DRIFT-678); the `recurrence` enum gates the
  // backend _expand_recurrence call-site, so it's set only on the off (clear)
  // path here — v1-faithful (v1 create has no recurrence-enum input).
  recurrence?: string;
  cadence_rrule?: string | null;
  recurrence_end_date?: string | null;
  // DRIFT-910 slice 4 (CHG-2483) — hero image FK (assets.id).
  hero_image_asset_id?: string | null;
}

export async function createEvent(payload: EventWritePayload): Promise<Event> {
  const r = await apiClient().post<Event>("/events", payload);
  return r.data;
}

export async function updateEvent(
  id: string,
  payload: EventWritePayload,
): Promise<Event> {
  const r = await apiClient().patch<Event>(`/events/${id}`, payload);
  return r.data;
}

export type EventPagedEnvelope = PagedEnvelope<Event>;

export interface EventAttendeeEntry {
  id: string;
  full_name: string;
  email: string;
  registered_at: string;
  status: "confirmed" | "waitlisted" | "canceled";
}

export interface EventDeliveryLogEntry {
  id: string;
  channel: "email" | "sms";
  occurred_at: string;
  subject: string;
  status: "delivered" | "bounced" | "failed" | "pending";
}

export interface ListEventsParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  status?: EventStatus;
  designation_id?: string;
  // CPE-FE2 (F7) — generic filters bag forwarded from the ListPage filter
  // row. Today only designation_id is surfaced (the v2 events list's
  // Designation dropdown); status is also accepted here for forward-compat.
  // The `/events?designation_id=` filter is honoured end-to-end after
  // CPE-020 (Finding 7).
  filters?: Record<string, string | number | boolean | undefined>;
}

export async function listEvents(
  params: ListEventsParams = {},
): Promise<{ items: Event[]; total: number }> {
  const filters = params.filters ?? {};
  const r = await apiClient().get<EventPagedEnvelope>("/events", {
    params: {
      offset: (params.page ?? 0) * (params.pageSize ?? 50),
      limit: params.pageSize ?? 50,
      sort: params.sortField
        ? `${params.sortField}:${params.sortDirection ?? "asc"}`
        : undefined,
      q: params.search,
      status: filters.status ?? params.status,
      designation_id: filters.designation_id ?? params.designation_id,
    },
  });
  return { items: r.data.items, total: r.data.total };
}

export async function getEvent(id: string): Promise<Event> {
  const r = await apiClient().get<Event>(`/events/${id}`);
  return r.data;
}

// Forward-promised: canonical /events/{id}/attendees ships later. The
// detail tab body renders empty-state copy + cites the future endpoint;
// when it lands, replace the function body with apiClient().get(...).
export async function getEventAttendees(
  id: string,
): Promise<EventAttendeeEntry[]> {
  void id;
  return [];
}

// Forward-promised: same pattern as attendees. Per entity-hint
// detail_v2.tabs.communications.data_source = GET /events/{id}/delivery-logs.
export async function getEventDeliveryLogs(
  id: string,
): Promise<EventDeliveryLogEntry[]> {
  void id;
  return [];
}

export const eventsApi = {
  listEvents,
  getEvent,
  createEvent,
  updateEvent,
  getEventAttendees,
  getEventDeliveryLogs,
};
